import { useState, useEffect } from 'react'
import { useT } from '../i18n/index.js'

const DISMISS_KEY = 'demo-banner-dismissed'

interface DemoStatus {
  isDemo: boolean
  expiresAt?: string
}

function getDaysRemaining(expiresAt: string): number {
  const now = new Date()
  const expires = new Date(expiresAt)
  const diffMs = expires.getTime() - now.getTime()
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
}

export function DemoBanner() {
  const t = useT()
  const [demoStatus, setDemoStatus] = useState<DemoStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) === 'true') {
      setDismissed(true)
      return
    }

    fetch('/api/demo/status')
      .then((res) => res.json())
      .then((data: DemoStatus) => setDemoStatus(data))
      .catch(() => setDemoStatus(null))
  }, [])

  if (dismissed || !demoStatus?.isDemo) {
    return null
  }

  const daysRemaining = demoStatus.expiresAt
    ? getDaysRemaining(demoStatus.expiresAt)
    : null

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true')
    setDismissed(true)
  }

  return (
    <>
    {/* Spacer to prevent layout shift under fixed banner (UX-LAY-10) */}
    <div className="h-[44px] shrink-0" aria-hidden="true" />
    <div
      className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center bg-amber-500 text-white min-h-[44px] px-4 py-2 text-sm font-medium shadow-md"
    >
      <span>
        {t('demo.title')}
        {daysRemaining !== null ? ` — ${t('demo.expiration').replace('{days}', String(daysRemaining))}` : ''}
        {' | '}
        <a
          href="/settings/billing"
          className="underline font-semibold hover:text-amber-100 transition-colors"
        >
          {t('demo.upgrade')} →
        </a>
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="ml-4 p-1 hover:bg-amber-600 rounded transition-colors focus:ring-2 focus:ring-white focus:ring-offset-1 focus:ring-offset-amber-500"
        aria-label={t('demo.closeAriaLabel')}
      >
        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
    </>
  )
}
